import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { anlagenAPI } from '../services/api';
import { ArrowLeft, QrCode as QrCodeIcon, AlertCircle } from 'lucide-react';
import Navbar from '../components/Navbar';

const QRScanner = () => {
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [scanner, setScanner] = useState(null);

  useEffect(() => {
    // Mobile-optimierte QR-Scanner Konfiguration
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    const html5QrcodeScanner = new Html5QrcodeScanner(
      "qr-reader",
      { 
        fps: 10,
        qrbox: isMobile ? { width: 280, height: 280 } : { width: 250, height: 250 },
        aspectRatio: 1.0,
        // Mobile-spezifische Einstellungen
        rememberLastUsedCamera: true,
        supportedScanTypes: [0, 1], // SCAN_TYPE_CAMERA und SCAN_TYPE_FILE
        showTorchButtonIfSupported: true, // Zeige Taschenlampe auf mobilen Geräten
        // Verbesserte Kamera-Auswahl für Mobile
        videoConstraints: {
          facingMode: { ideal: "environment" }, // Rückseitige Kamera bevorzugen
          width: { ideal: isMobile ? 1920 : 1280 },
          height: { ideal: isMobile ? 1080 : 720 }
        }
      },
      false
    );

    html5QrcodeScanner.render(onScanSuccess, onScanError);
    setScanner(html5QrcodeScanner);
    setScanning(true);

    return () => {
      if (html5QrcodeScanner) {
        html5QrcodeScanner.clear().catch(err => console.error('Error clearing scanner:', err));
      }
    };
  }, []);

  const onScanSuccess = async (decodedText) => {
    setScanning(false);
    if (scanner) {
      scanner.clear();
    }

    try {
      const anlage = await anlagenAPI.getByQR(decodedText);
      navigate(`/anlage/${anlage.id}`);
    } catch (error) {
      console.error('Fehler beim Laden der Anlage:', error);
      setError('QR-Code konnte keiner Anlage zugeordnet werden. Bitte versuchen Sie es erneut.');
      setScanning(true);
    }
  };

  const onScanError = (errorMessage) => {
    // Ignore scanning errors (they happen constantly while searching for QR code)
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </button>

        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex flex-col items-center mb-6">
            <div className="bg-blue-100 p-4 rounded-full mb-4">
              <QrCodeIcon className="w-12 h-12 text-blue-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="scanner-title">QR-Code scannen</h1>
            <p className="text-gray-600 text-center">
              Richten Sie die Kamera auf den QR-Code der Anlage
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div id="qr-reader" className="mb-6" data-testid="qr-reader"></div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2">Hinweise:</h3>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>Halten Sie das Gerät ruhig</li>
              <li>Sorgen Sie für gute Beleuchtung</li>
              <li>Der QR-Code sollte vollständig im Rahmen sichtbar sein</li>
              <li>Nach erfolgreichem Scan werden Sie zur Anlage weitergeleitet</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScanner;